import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { callAPI, setTokenExpired } from '../components/utils';
import {
  loadRecaptchaScript,
  executeRecaptcha,
} from '../components/security/recaptcha';
import {
  Box,
  Typography,
  Link as MuiLink,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  MailOutline,
  PhoneOutlined,
} from '@mui/icons-material';
import TextInput from '../components/ui/textInput';
import Button from '../components/ui/button';
import Dialog from '../components/ui/dialog';
import Alert from '../components/ui/alert';
import AuthLayout from '../components/layouts/authLayout';
import TwoFactorAuth from '../components/security/twoFactorAuth';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [openHelpDialog, setOpenHelpDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  const handleLogin = async (captchaToken) => {
    setLoading(true);
    try {
      setTokenExpired(false);

      await callAPI({
        request: 'login',
        method: 'post',
        params: {
          email,
          password,
          twoFactorToken: captchaToken,
        },
        onSuccess: (response) => {
          if (response.data.requireOTP) {
            setShowTwoFactor(true);
          } else {
            localStorage.setItem('authToken', response.data.accessToken);
            navigate('/');
          }
        },
        onError: (error) => {
          setErrorMessage(error || 'An error occurred during login.');
          console.error('Login Error:', error);
        },
      });
    } catch (error) {
      setErrorMessage('An error occurred during login.');
      console.error('Login API Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    const captchaToken = await executeRecaptcha();

    handleLogin(captchaToken);
  };

  const handleTwoFactorSuccess = (token) => {
    if (token) localStorage.setItem('authToken', token);
    setShowTwoFactor(false);
    navigate('/');
  };

  return (
    <AuthLayout
      title="Welcome Back!"
      description="Enter your email and password to continue"
    >
      <form onSubmit={handleFormSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            label="Email"
            placeholder="Enter your email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextInput
            label="Password"
            placeholder="Enter your password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            trailingIcon={showPassword ? <Visibility /> : <VisibilityOff />}
            onIconClick={() => setShowPassword(!showPassword)}
            required
          />
          <Box display="flex" justifyContent="flex-end">
            <MuiLink
              component={Link}
              to="/forgot-password"
              underline="hover"
              sx={{ fontWeight: '600' }}
            >
              Forgot Password?
            </MuiLink>
          </Box>
          {errorMessage && (
            <Alert 
              type="error"
              message={errorMessage}
            />
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            bgColor="#172242"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                size={24}
                sx={{
                  color: 'var(--navyBlue-default)',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            ) : (
              'Log In'
            )}
          </Button>
        </Stack>
      </form>

      <Divider sx={{ bgcolor: 'var(--gray-300)', my: 3 }} />

      <Typography textAlign="center">
        Have Trouble Logging In?{' '}
        <MuiLink
          component="button"
          onClick={() => setOpenHelpDialog(true)}
          underline="hover"
          sx={{ fontWeight: 'bold' }}
        >
          Ask an Admin
        </MuiLink>
      </Typography>
      <Dialog
        open={openHelpDialog}
        onClose={() => setOpenHelpDialog(false)}
        title="Need Help?"
        description=" Contact our admin team for any enquiries:"
        hideCancel
        hideConfirm
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneOutlined sx={{ color: 'var(--navyBlue-light)' }} />
          <Typography variant="body1">0480 041 747</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MailOutline sx={{ color: 'var(--navyBlue-light)' }} />
          <MuiLink
            href="mailto:Enquiries@tmjtutoring.com.au"
            sx={{
              textDecoration: 'none',
              color: 'var(--black)',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Enquiries@tmjtutoring.com.au
          </MuiLink>
        </Box>
      </Dialog>
      
      <Dialog
        open={showTwoFactor}
        onClose={() => setShowTwoFactor(false)}
        title="Authentication"
        description="Enter the code from your authenticator app"
        hideCancel
        hideConfirm
      >
        <TwoFactorAuth
          email={email}
          password={password}
          onSuccess={handleTwoFactorSuccess}
          buttonBgColor="#172242"
        />
      </Dialog>
    </AuthLayout>
  );
};

export default Login;
